import classNames from "classnames";
import fontColorContrast from "font-color-contrast";
import { DateTime } from "luxon";
import { Fragment, ReactNode } from "react";
import { common_utils } from "../../../../../../../../../common/common_utils";
import { Constants } from "../../../../../../../../../common/constants";
import { EventDisplayData } from "../../../../../../../../../common/sesh_types";
import { time_format_utils } from "../../../../../../../../../common/time_format_utils";
import { NativeEventDisplayData } from "../../../../../../../../../src/shard_api/routes/getNativeEvents";
import { RemovingThrobber } from "../../../../../removing_throbber";
import { DisableableLink } from "../../../../base_components/disableable_link";
import { RSVPListImproved } from "../../../../base_components/rsvp_list_improved";
import { DropdownItemToElementWithoutLabel } from "../../../../base_components/with_dropdown_improved";
import { BoxItem } from "../../../../box_item";
import { ChannelName, ChannelNameWithoutCache } from "../../../../channel_name";
import { useGenerateEventActions } from "../../../../hooks/use_generate_event_actions";
import { useServerId } from "../../../../hooks/use_server_id";
import SimpleMemberDisplayName, {
	SimpleMemberDisplayNameWithoutCache,
} from "../../../../member_display_name/simple_member_display_name";
import { WithTooltipPortaled } from "../../../../tooltips/with_tooltip_portaled";
import { useHistoryPusher } from "../hooks/use_history_pusher";
import { usePublicCalendarContext } from "../hooks/use_public_calendar_context";
import { DashboardOrPublicLink } from "./dashboard_or_public_link";

function BaseEventItemDropdown(props: {
	color: string;
	actions: JSX.Element;
	name: string;
	author_id: string;
	children: ReactNode;
	is_discord_event?: boolean;
}) {
	return (
		<BoxItem
			bgColor="semidark"
			style={{
				borderColor: props.color,
			}}
			className="overflow-hidden rounded-lg !p-0 pt-0 shadow-lg"
		>
			<div className="flex w-[90vw] flex-col sm:w-[28rem]">
				<div
					className="flex w-full items-center px-2"
					style={{ backgroundColor: props.color }}
				>
					{props.is_discord_event && (
						<span className="text-xl">
							<i className="fab fa-discord text-white"></i>
						</span>
					)}
					<div className="ml-auto flex items-center gap-1">
						{props.actions}
					</div>
				</div>
				<div className="flex flex-col px-4 py-2">
					<div className="text-xl font-bold sm:max-h-16 sm:overflow-auto sm:text-2xl">
						{props.name}
					</div>
					<div className="mt-2 flex flex-col gap-0 text-xs sm:gap-2 sm:text-sm">
						{props.children}
					</div>
					<div className="ml-auto mt-0 text-xs sm:mt-2">
						<EventItemMemberDisplayName
							author_id={props.author_id}
						/>
					</div>
				</div>
			</div>
		</BoxItem>
	);
}

export function SeshEventItemDropdown(props: {
	event: EventDisplayData;
	is_removing: boolean;
	setIsRemoving: (is_removing: boolean) => void;
}) {
	const server_id = useServerId();

	const start_time = DateTime.fromJSDate(
		new Date(props.event.start_time_str)
	);

	const end_time = DateTime.fromJSDate(new Date(props.event.end_time_str));

	return (
		<BaseEventItemDropdown
			actions={
				<EventItemDropdownActions
					event={props.event}
					is_removing={props.is_removing}
					setIsRemoving={props.setIsRemoving}
				/>
			}
			name={props.event.name}
			author_id={props.event.author_id}
			color={props.event.color}
		>
			<EventItemDropdownAttribute
				icon="fas fa-clock"
				title="Start Time"
				text={`${time_format_utils.displayDateTime(start_time, {
					date: true,
					time: true,
					weekday: true,
				})} – ${time_format_utils.displayDateTime(end_time, {
					time: true,
					date: start_time.day !== end_time.day,
					weekday: start_time.day !== end_time.day,
				})}`}
			/>
			{props.event.rrule && (
				<EventItemDropdownAttribute
					icon="fas fa-redo-alt"
					title="Repeats"
					text={time_format_utils.formatRRule(
						props.event.rrule,
						DateTime.local().zoneName,
						DateTime.fromJSDate(
							new Date(props.event.start_time_str)
						)
					)}
				/>
			)}
			<EventItemChannelAttribute channel_id={props.event.channel_id} />
			<EventItemDropdownAttribute
				icon="fas fa-user"
				title="RSVPs"
				text={
					props.event.is_repeat_occurrence ? (
						<WithTooltipPortaled
							style="Info"
							tooltip="Event not posted yet"
						>
							<RSVPListImproved
								preset={props.event.rsvp_options}
							/>
						</WithTooltipPortaled>
					) : (
						<DashboardOrPublicLink
							to={common_utils.getAttendeesDisplayURL(
								server_id,
								props.event.message_id,
								false
							)}
						>
							<RSVPListImproved
								preset={props.event.rsvp_options}
							/>
						</DashboardOrPublicLink>
					)
				}
			/>
			{props.event.description && (
				<EventItemDropdownAttribute
					icon="fas fa-align-left"
					title="Description"
					text={
						props.event.description ? (
							props.event.description
						) : (
							<i className="italic">None</i>
						)
					}
				/>
			)}
		</BaseEventItemDropdown>
	);
}

export function DiscordEventItemDropdown(props: {
	event: NativeEventDisplayData;
	is_removing: boolean;
	setIsRemoving: (is_removing: boolean) => void;
}) {
	const server_id = useServerId();

	const start_time = DateTime.fromJSDate(
		new Date(props.event.start_time_str)
	);

	const start_time_text = [
		time_format_utils.displayDateTime(start_time, {
			date: true,
			time: true,
			weekday: true,
		}),
	];

	if (props.event.end_time_str) {
		start_time_text.push(
			time_format_utils.displayDateTime(
				DateTime.fromJSDate(new Date(props.event.end_time_str)),
				{
					time: true,
				}
			)
		);
	}

	return (
		<BaseEventItemDropdown
			is_discord_event
			actions={
				<WithTooltipPortaled style="Info" tooltip={"Open in Discord"}>
					<div className="flex">
						<a
							className="p-2 text-white opacity-100 transition-all hover:opacity-60"
							href={`https://discord.com/events/${server_id}/${props.event.id}`}
						>
							<i className="fas fa-external-link-alt" />
						</a>
					</div>
				</WithTooltipPortaled>
			}
			name={props.event.name}
			author_id={props.event.author_id}
			color={Constants.DISCORD_BLURPLE}
		>
			<EventItemDropdownAttribute
				icon="fas fa-clock"
				title="Start Time"
				text={start_time_text.join(" – ")}
			/>
			<EventItemDropdownAttribute
				icon="fas fa-user"
				title="RSVPs"
				text={props.event.attendees.toString()}
			/>
			{props.event.type === "External" && props.event.location && (
				<EventItemDropdownAttribute
					icon="fas fa-map-marker-alt"
					title="Location"
					text={props.event.location}
				/>
			)}
			{props.event.type === "Voice" && (
				<EventItemDropdownAttribute
					icon="fas fa-volume-up"
					title="Location"
					text={props.event.channel_name}
				/>
			)}
			{props.event.type === "Stage" && (
				<EventItemDropdownAttribute
					icon="fa fa-podcast"
					title="Location"
					text={props.event.channel_name}
				/>
			)}
			{props.event.description && (
				<EventItemDropdownAttribute
					icon="fas fa-align-left"
					title="Description"
					text={
						props.event.description ? (
							props.event.description
						) : (
							<i className="italic">None</i>
						)
					}
				/>
			)}
		</BaseEventItemDropdown>
	);
}

function EventItemChannelAttribute({ channel_id }: { channel_id: string }) {
	const calendar_context = usePublicCalendarContext();

	let attribute_text: JSX.Element | undefined = undefined;
	if (calendar_context.is_public) {
		// If we're on public calendar, we need to use different API routes. This component facilitates that
		const channel = calendar_context.channels.find(
			(val) => val.id === channel_id
		);

		attribute_text = (
			<ChannelNameWithoutCache
				channel_name={channel?.name ?? channel_id}
			/>
		);
	} else {
		attribute_text = (
			<ChannelName channel_id={channel_id} include_hashtag={false} />
		);
	}

	return (
		<EventItemDropdownAttribute
			icon="fas fa-hashtag"
			title="Channel"
			text={attribute_text}
		/>
	);
}

function EventItemMemberDisplayName({ author_id }: { author_id: string }) {
	const calendar_context = usePublicCalendarContext();

	if (calendar_context.is_public) {
		// If we're on public calendar, we need to use different API routes. This component facilitates that
		const member = calendar_context.authors.find(
			(val) => val.id === author_id
		);

		return (
			<SimpleMemberDisplayNameWithoutCache
				member={member}
				user_id={author_id}
				include_avatar
				width={16}
			/>
		);
	}

	return (
		<SimpleMemberDisplayName
			user_id={author_id}
			include_avatar
			width={16}
		/>
	);
}

function EventItemDropdownAttribute(props: {
	title: string;
	icon: string;
	text: JSX.Element | string;
}) {
	return (
		<div className="flex items-start gap-2">
			<div className="h-6 w-4" title={props.title}>
				<i
					className={classNames("text-content-secondary", props.icon)}
				></i>
			</div>
			<span className="max-h-48 overflow-auto whitespace-pre-wrap">
				{props.text}
			</span>
		</div>
	);
}
function EventItemDropdownActions(props: {
	event: EventDisplayData;
	is_removing: boolean;
	setIsRemoving: (is_removing: boolean) => void;
}) {
	const actions = useGenerateEventActions(
		props.event,
		props.is_removing,
		props.setIsRemoving,
		`${props.event.message_id}_${props.event.start_time_str}`
	);

	const color_contrast = fontColorContrast(props.event.color);

	if (actions.isLoading === true) {
		return (
			<div className="px-4 py-3">
				<RemovingThrobber color={color_contrast} />
			</div>
		);
	}

	const DropdownItemsWithColor =
		DropdownItemToElementWithoutLabel(color_contrast);

	return (
		<div className="flex gap-2">
			<div
				className="flex items-center gap-1"
				style={{
					color: color_contrast,
				}}
			>
				{Object.keys(actions)
					.filter((val) => actions[val])
					.map((val) => {
						if (val === "isLoading") {
							return;
						}

						return (
							<Fragment key={val}>
								{DropdownItemsWithColor[actions[val].type](
									actions[val]
								)}
							</Fragment>
						);
					})}
			</div>
		</div>
	);
}
